import React, { useState } from 'react';
import '../../styles/Tables.css';
import { useTranslation } from 'react-i18next';

function RecentTradesTable({ trades }) {
  const { t } = useTranslation();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterText, setFilterText] = useState('');
  const [selectedAction, setSelectedAction] = useState('all');
  
  const formatDate = (dateString) => {
    // If the dateString is already just a date (YYYY-MM-DD), return it as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Otherwise, parse the date and format it
    try {
      const date = new Date(dateString);
      
      // Format options for date only (no time)
      const options = {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
      };
      
      return new Intl.DateTimeFormat('en-US', options).format(date);
    } catch (e) {
      console.error("Error formatting date:", e);
      return dateString; // Return original if parsing fails
    }
  };
  
  // Sorting function
  const sortData = (data, key, direction) => {
    return [...data].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };
  
  // Handle sort
  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };
  
  // Filter data
  const filteredData = trades.filter(trade => {
    const matchesText = trade.symbol.toLowerCase().includes(filterText.toLowerCase());
    const matchesAction = selectedAction === 'all' || trade.side.toLowerCase() === selectedAction;
    return matchesText && matchesAction;
  });
  
  // Sort filtered data
  const sortedData = sortConfig.key ? sortData(filteredData, sortConfig.key, sortConfig.direction) : filteredData;
  
  // Calculate stats
  const totalTrades = sortedData.length;
  const buyTrades = sortedData.filter(t => t.side.toLowerCase() === 'buy').length;
  const sellTrades = sortedData.filter(t => t.side.toLowerCase() === 'sell').length;
  
  return (
    <div className="fancy-portfolio-container">
      {/* Trades Header */}
      <div className="portfolio-header">
        <div className="header-content">
          <div className="portfolio-stats">
            <div className="stat-badge total">
              <span className="stat-number">{totalTrades}</span>
              <span className="stat-label">{t('recentTrades.totalTrades', 'Total Trades')}</span>
            </div>
            <div className="stat-badge long">
              <span className="stat-number">{buyTrades}</span>
              <span className="stat-label">{t('recentTrades.buyTrades', 'Buy Trades')}</span>
            </div>
            <div className="stat-badge short">
              <span className="stat-number">{sellTrades}</span>
              <span className="stat-label">{t('recentTrades.sellTrades', 'Sell Trades')}</span>
            </div>
            <div className="search-badge">
              <div className="search-container">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder={t('recentTrades.searchPlaceholder', 'Search symbols...')}
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  className="search-input"
                />
                {filterText && (
                  <button 
                    className="clear-search"
                    onClick={() => setFilterText('')}
                    title={t('recentTrades.clearSearch', 'Clear search')}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Action Filter */}
      <div className="filter-section">
        <div className="filter-container">
          <span className="filter-label">{t('recentTrades.filterBy', 'Filter by:')}</span>
          <div className="action-filters">
            <button 
              className={`action-filter-btn ${selectedAction === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedAction('all')}
            >
              📊 {t('recentTrades.allActions', 'All')}
            </button>
            <button 
              className={`action-filter-btn ${selectedAction === 'buy' ? 'active' : ''}`}
              onClick={() => setSelectedAction('buy')}
            >
              📈 {t('recentTrades.buyOnly', 'Buy')}
            </button>
            <button 
              className={`action-filter-btn ${selectedAction === 'sell' ? 'active' : ''}`}
              onClick={() => setSelectedAction('sell')}
            >
              📉 {t('recentTrades.sellOnly', 'Sell')}
            </button>
          </div>
        </div>
      </div>
      
      {/* Enhanced Table */}
      <div className="fancy-table-container">
        <table className="fancy-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('side')} className="sortable-header">
                <div className="header-content">
                  <span>{t('recentTrades.action')}</span>
                  {sortConfig.key === 'side' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th onClick={() => handleSort('symbol')} className="sortable-header">
                <div className="header-content">
                  <span>{t('recentTrades.symbol')}</span>
                  {sortConfig.key === 'symbol' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th onClick={() => handleSort('time')} className="sortable-header">
                <div className="header-content">
                  <span>{t('recentTrades.date')}</span>
                  {sortConfig.key === 'time' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th className="sortable-header">
                <div className="header-content">
                  <span>{t('recentTrades.aiTrigger')}</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.length > 0 ? (
              sortedData.map((trade) => (
                <tr key={trade.id} className="holding-row">
                  <td>
                    <div className="action-cell">
                      <span className={`action-badge ${trade.side.toLowerCase()}`}>
                        <span className="action-icon">
                          {trade.side.toLowerCase() === 'buy' ? '📈' : '📉'}
                        </span>
                        <span className="action-text">{trade.side}</span>
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="symbol-cell">
                      <div className="symbol-badge">
                        <span className="symbol-text">{trade.symbol}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="date-cell">
                      <span className="date-value">{formatDate(trade.time)}</span>
                    </div>
                  </td>
                  <td>
                    <div className="trigger-cell">
                      <span className={`trigger-badge ${trade.side.toLowerCase() === 'buy' ? 'bullish' : 'bearish'}`}>
                        <span className="trigger-icon">
                          {trade.side.toLowerCase() === 'buy' ? '🚀' : '⚠️'}
                        </span>
                        <span className="trigger-text">
                          {trade.side.toLowerCase() === 'buy' 
                            ? t('recentTrades.bullishPattern') 
                            : t('recentTrades.bearishDivergence')}
                        </span>
                      </span>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr className="empty-state">
                <td colSpan="4">
                  <div className="empty-content">
                    <span className="empty-icon">📭</span>
                    <p className="empty-text">
                      {filterText || selectedAction !== 'all'
                        ? t('recentTrades.noResults', 'No trades match your filters')
                        : t('recentTrades.noTrades', 'No recent trades available')
                      }
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Trades Footer */}
      <div className="portfolio-footer">
        <div className="footer-info">
          <span className="footer-text">
            {t('recentTrades.lastUpdated')}: {new Date().toLocaleString()}
          </span>
        </div>
        <div className="footer-actions">
          <button className="footer-btn" onClick={() => window.location.reload()}>
            <span className="btn-icon">🔄</span>
            <span className="btn-text">{t('recentTrades.refresh', 'Refresh')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default RecentTradesTable; 